const DEFAULT_BIN_COUNT = 10;
const DEFAULT_TOP_VALUE_COUNT = 8;

function isMissing(value) {
  return value === null || value === undefined || value === '';
}

function finiteNumber(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function dateTimestamp(value) {
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  if (
    typeof value !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}(?:[t\s].*)?$/i.test(value.trim())
  ) {
    return null;
  }
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function stableValueKey(value) {
  if (value && typeof value === 'object') {
    try {
      return `object:${JSON.stringify(value)}`;
    } catch (_error) {
      return `object:${String(value)}`;
    }
  }
  return `${typeof value}:${String(value)}`;
}

function displayValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'Missing';
  }
  if (value && typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_error) {
      return String(value);
    }
  }
  return String(value);
}

function percentile(sortedValues, fraction) {
  if (!sortedValues.length) {
    return null;
  }
  if (sortedValues.length === 1) {
    return sortedValues[0];
  }
  const position = (sortedValues.length - 1) * fraction;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);
  if (lowerIndex === upperIndex) {
    return sortedValues[lowerIndex];
  }
  const weight = position - lowerIndex;
  return sortedValues[lowerIndex] * (1 - weight) + sortedValues[upperIndex] * weight;
}

function formatCompactNumber(value) {
  if (!Number.isFinite(value)) {
    return 'n/a';
  }
  const magnitude = Math.abs(value);
  if (magnitude >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')}B`;
  }
  if (magnitude >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
  }
  if (magnitude >= 1_000) {
    return `${(value / 1_000).toFixed(2).replace(/\.?0+$/, '')}K`;
  }
  if (magnitude > 0 && magnitude < 0.01) {
    return value.toExponential(2);
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function numericHistogram(values, binCount = DEFAULT_BIN_COUNT) {
  if (!values.length) {
    return [];
  }
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  if (minimum === maximum) {
    return [{ label: formatCompactNumber(minimum), count: values.length }];
  }
  const safeBinCount = Math.max(2, Math.min(20, Number(binCount) || DEFAULT_BIN_COUNT));
  const width = (maximum - minimum) / safeBinCount;
  const counts = new Array(safeBinCount).fill(0);
  values.forEach(value => {
    const rawIndex = Math.floor((value - minimum) / width);
    counts[Math.min(safeBinCount - 1, Math.max(0, rawIndex))] += 1;
  });
  return counts.map((count, index) => {
    const lower = minimum + index * width;
    const upper = index === safeBinCount - 1 ? maximum : lower + width;
    return {
      label: `${formatCompactNumber(lower)}–${formatCompactNumber(upper)}`,
      count
    };
  });
}

function valueFrequencies(values, limit = DEFAULT_TOP_VALUE_COUNT) {
  const frequencyByKey = new Map();
  values.forEach(value => {
    const key = stableValueKey(value);
    const current = frequencyByKey.get(key);
    if (current) {
      current.count += 1;
    } else {
      frequencyByKey.set(key, { value, count: 1 });
    }
  });
  return Array.from(frequencyByKey.values())
    .sort((left, right) => right.count - left.count || displayValue(left.value).localeCompare(displayValue(right.value)))
    .slice(0, Math.max(1, Number(limit) || DEFAULT_TOP_VALUE_COUNT))
    .map(item => ({ label: displayValue(item.value), count: item.count }));
}

function inferProfileKind(values) {
  if (!values.length) {
    return 'empty';
  }
  let numericCount = 0;
  let datetimeCount = 0;
  let booleanCount = 0;
  values.forEach(value => {
    if (finiteNumber(value) !== null) {
      numericCount += 1;
    }
    if (dateTimestamp(value) !== null) {
      datetimeCount += 1;
    }
    if (typeof value === 'boolean') {
      booleanCount += 1;
    }
  });
  const threshold = Math.max(1, Math.ceil(values.length * 0.8));
  if (booleanCount === values.length) {
    return 'boolean';
  }
  if (numericCount >= threshold) {
    return 'numeric';
  }
  if (datetimeCount >= threshold) {
    return 'datetime';
  }
  return 'categorical';
}

function profileColumn(columnName, rows) {
  const rawValues = rows.map(row => (row && typeof row === 'object' ? row[columnName] : null));
  const values = rawValues.filter(value => !isMissing(value));
  const kind = inferProfileKind(values);
  const distinctCount = new Set(values.map(stableValueKey)).size;
  const missingCount = rawValues.length - values.length;
  const base = {
    name: String(columnName),
    kind,
    rowCount: rawValues.length,
    populatedCount: values.length,
    missingCount,
    missingPercent: rawValues.length ? (missingCount / rawValues.length) * 100 : 0,
    distinctCount,
    metrics: [],
    bars: []
  };

  if (kind === 'numeric') {
    const numbers = values.map(finiteNumber).filter(value => value !== null).sort((left, right) => left - right);
    const sum = numbers.reduce((total, value) => total + value, 0);
    return {
      ...base,
      metrics: [
        { label: 'Min', value: formatCompactNumber(numbers[0]) },
        { label: 'Median', value: formatCompactNumber(percentile(numbers, 0.5)) },
        { label: 'Mean', value: formatCompactNumber(sum / numbers.length) },
        { label: 'Max', value: formatCompactNumber(numbers[numbers.length - 1]) }
      ],
      bars: numericHistogram(numbers)
    };
  }

  if (kind === 'datetime') {
    const timestamps = values
      .map(dateTimestamp)
      .filter(value => value !== null)
      .sort((left, right) => left - right);
    return {
      ...base,
      metrics: [
        { label: 'Earliest', value: new Date(timestamps[0]).toISOString().slice(0, 10) },
        { label: 'Latest', value: new Date(timestamps[timestamps.length - 1]).toISOString().slice(0, 10) }
      ],
      bars: valueFrequencies(values)
    };
  }

  return {
    ...base,
    metrics: [
      {
        label: 'Most common',
        value: valueFrequencies(values, 1)[0]?.label || 'n/a'
      }
    ],
    bars: valueFrequencies(values)
  };
}

export function profileResultColumns(result, maxColumns = 24) {
  const rows = result && Array.isArray(result.data) ? result.data : [];
  const columns = result && Array.isArray(result.columns) ? result.columns : [];
  return columns
    .slice(0, Math.max(1, Number(maxColumns) || 24))
    .map(columnName => profileColumn(columnName, rows));
}

function quoteIdentifierPart(value, sourceType) {
  const text = String(value || '').replace(/\0/g, '').trim();
  if (!text) {
    return '';
  }
  if (String(sourceType || '').toLowerCase().includes('mysql')) {
    return `\`${text.replace(/`/g, '``')}\``;
  }
  return `"${text.replace(/"/g, '""')}"`;
}

export function quoteQualifiedIdentifier(value, sourceType = '') {
  return String(value || '')
    .split('.')
    .map(part => quoteIdentifierPart(part, sourceType))
    .filter(Boolean)
    .join('.');
}

function profileSourceSql({ entityName = '', originalQuery = '', sourceType = '' } = {}) {
  const query = String(originalQuery || '').trim().replace(/;+\s*$/, '');
  if (query) {
    return {
      prefix: `WITH profile_source AS (\n${query}\n)\n`,
      relation: 'profile_source'
    };
  }
  const relation = quoteQualifiedIdentifier(entityName, sourceType);
  return { prefix: '', relation };
}

export function buildDistributionSql({
  columnName,
  kind,
  entityName = '',
  originalQuery = '',
  sourceType = ''
} = {}) {
  const column = quoteQualifiedIdentifier(columnName, sourceType);
  const source = profileSourceSql({ entityName, originalQuery, sourceType });
  if (!column || !source.relation) {
    return '';
  }

  if (kind === 'numeric') {
    return `${source.prefix}SELECT
  COUNT(*) AS row_count,
  COUNT(${column}) AS populated_count,
  COUNT(DISTINCT ${column}) AS distinct_count,
  MIN(${column}) AS min_value,
  AVG(${column}) AS avg_value,
  MAX(${column}) AS max_value
FROM ${source.relation};`;
  }

  return `${source.prefix}SELECT
  ${column} AS value,
  COUNT(*) AS row_count
FROM ${source.relation}
GROUP BY ${column}
ORDER BY row_count DESC
LIMIT 20;`;
}

export { formatCompactNumber, inferProfileKind, numericHistogram, valueFrequencies };
